export function PrivacyPolicyPage() {
  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "48px 24px" }}>
      <h1 className="text-3xl font-medium font-display tracking-tight mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-8">Last updated: 5 March 2026</p>

      <div className="space-y-8 text-sm leading-relaxed text-foreground">
        <section>
          <h2 className="text-lg font-semibold mb-3">1. Who We Are</h2>
          <p>
            Stayframe ("we", "us", "our") provides property intelligence software for short-term rental hosts.
            This policy explains how we collect, use, and protect personal data when you use our platform at stayframe.ai.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">2. Data We Collect</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-border rounded-md">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-2 border-b border-border font-medium">Category</th>
                  <th className="text-left p-2 border-b border-border font-medium">Data</th>
                  <th className="text-left p-2 border-b border-border font-medium">Legal Basis</th>
                  <th className="text-left p-2 border-b border-border font-medium">Retention</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-2">Account</td>
                  <td className="p-2">Email, name, password hash</td>
                  <td className="p-2">Contract (Art. 6(1)(b))</td>
                  <td className="p-2">Until account deletion</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Auth sessions</td>
                  <td className="p-2">IP address, user agent, timestamps</td>
                  <td className="p-2">Legitimate interest (Art. 6(1)(f))</td>
                  <td className="p-2">90 days (auto-purge)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Auth tokens</td>
                  <td className="p-2">Email, IP, user agent</td>
                  <td className="p-2">Contract</td>
                  <td className="p-2">24 hours (auto-expire)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Property config</td>
                  <td className="p-2">Property name, location, timezone, PMS keys</td>
                  <td className="p-2">Contract</td>
                  <td className="p-2">Until property deletion</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Booking data</td>
                  <td className="p-2">Guest names, countries, dates, party size, amounts</td>
                  <td className="p-2">Legitimate interest of host (controller)</td>
                  <td className="p-2">Until anonymised or property deleted</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Review data</td>
                  <td className="p-2">Reviewer names, locations, ratings, review text</td>
                  <td className="p-2">Legitimate interest</td>
                  <td className="p-2">Until property deletion</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">LLM usage</td>
                  <td className="p-2">Model, tokens, cost, surface (no prompts/responses stored)</td>
                  <td className="p-2">Legitimate interest</td>
                  <td className="p-2">180 days (auto-purge)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Guidebook sessions</td>
                  <td className="p-2">IP address, user agent</td>
                  <td className="p-2">Legitimate interest</td>
                  <td className="p-2">90 days (auto-purge)</td>
                </tr>
                <tr>
                  <td className="p-2">Job/error logs</td>
                  <td className="p-2">Error messages, job metadata</td>
                  <td className="p-2">Legitimate interest</td>
                  <td className="p-2">90-180 days (auto-purge)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">3. Data Processing Roles</h2>
          <p className="mb-2">
            <strong>Stayframe as Processor:</strong> When processing guest booking data, review data, and calendar data on behalf of property hosts. The host is the Controller. A Data Processing Agreement (DPA) is available on request.
          </p>
          <p>
            <strong>Stayframe as Controller:</strong> For host account data, billing data, and platform usage analytics.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">4. Sub-processors</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-border rounded-md">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-2 border-b border-border font-medium">Sub-processor</th>
                  <th className="text-left p-2 border-b border-border font-medium">Purpose</th>
                  <th className="text-left p-2 border-b border-border font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-2">Cloudflare</td>
                  <td className="p-2">Infrastructure (Workers, D1, KV, Pages)</td>
                  <td className="p-2">Global (EU data residency available)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Anthropic (Claude)</td>
                  <td className="p-2">AI content generation</td>
                  <td className="p-2">US</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">OpenAI</td>
                  <td className="p-2">AI content generation (fallback)</td>
                  <td className="p-2">US</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Stripe</td>
                  <td className="p-2">Payment processing</td>
                  <td className="p-2">US (EU entity available)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Sentry</td>
                  <td className="p-2">Error monitoring (sendDefaultPii: false)</td>
                  <td className="p-2">EU (Frankfurt)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Postmark</td>
                  <td className="p-2">Transactional email</td>
                  <td className="p-2">US</td>
                </tr>
                <tr>
                  <td className="p-2">Apify</td>
                  <td className="p-2">Review data collection</td>
                  <td className="p-2">EU (Czech Republic)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">5. LLM Data Handling</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Guest first names only are sent to LLM providers for personalised content generation.</li>
            <li>No guest surnames, countries, financial data, or booking details are sent.</li>
            <li>Prompts and responses are NOT stored in Stayframe's database.</li>
            <li>LLM providers process data under their own DPAs with Stayframe.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">6. Your Rights (GDPR Articles 15-22)</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Access (Art. 15):</strong> Export all your data via Settings &gt; Privacy &gt; Export Data.
            </li>
            <li>
              <strong>Rectification (Art. 16):</strong> Update your profile via Settings. Contact support for corrections to system-generated data.
            </li>
            <li>
              <strong>Erasure (Art. 17):</strong> Delete your account via Settings &gt; Privacy &gt; Delete Account. This permanently removes all data including properties, bookings, reviews, and configuration.
            </li>
            <li>
              <strong>Restriction (Art. 18):</strong> Contact support to restrict processing while a complaint is resolved.
            </li>
            <li>
              <strong>Portability (Art. 20):</strong> Export your data as JSON via Settings &gt; Privacy &gt; Export Data.
            </li>
            <li>
              <strong>Objection (Art. 21):</strong> Contact support to object to processing based on legitimate interest.
            </li>
            <li>
              <strong>Automated decisions (Art. 22):</strong> Stayframe uses AI to generate content but makes no automated decisions with legal or significant effects.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">7. Guest Data Anonymisation</h2>
          <p>
            Property hosts can anonymise guest booking data older than a specified date via Settings &gt; Privacy &gt; Anonymise Guest Data. This replaces guest names with "Guest" and removes country data while preserving analytical metrics (dates, revenue, party size).
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">8. Data Retention Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-border rounded-md">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-2 border-b border-border font-medium">Data Type</th>
                  <th className="text-left p-2 border-b border-border font-medium">Retention</th>
                  <th className="text-left p-2 border-b border-border font-medium">Deletion Trigger</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-2">Auth sessions</td>
                  <td className="p-2">90 days</td>
                  <td className="p-2">Automatic purge</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Auth tokens</td>
                  <td className="p-2">24 hours</td>
                  <td className="p-2">Automatic expire</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">LLM usage logs</td>
                  <td className="p-2">180 days</td>
                  <td className="p-2">Automatic purge</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Guidebook sessions</td>
                  <td className="p-2">90 days</td>
                  <td className="p-2">Automatic purge</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Job/error logs</td>
                  <td className="p-2">90-180 days</td>
                  <td className="p-2">Automatic purge</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Booking data</td>
                  <td className="p-2">Indefinite</td>
                  <td className="p-2">Manual anonymise or account deletion</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-2">Review data</td>
                  <td className="p-2">Indefinite</td>
                  <td className="p-2">Account deletion</td>
                </tr>
                <tr>
                  <td className="p-2">Account data</td>
                  <td className="p-2">Indefinite</td>
                  <td className="p-2">Account deletion</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">9. Contact</h2>
          <p>
            Data Protection inquiries:{" "}
            <a href="mailto:privacy@stayframe.ai" className="underline hover:text-primary">
              privacy@stayframe.ai
            </a>
          </p>
          <p>
            Data Processing Agreement requests:{" "}
            <a href="mailto:dpa@stayframe.ai" className="underline hover:text-primary">
              dpa@stayframe.ai
            </a>
          </p>
        </section>
      </div>
    </div>
  )
}
